import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

// Contexto OTIMISTA para mensagens de 1 ticket (scoped por ticket):
// Permite componentes FILHOS do Ticket (ex.: FigurinhaPicker dentro de
// MessageInput) "injetar" uma mensagem fake já renderizada no MessagesList
// NO MESMO INSTANTE do clique, sem esperar backend/socket. Depois quando a
// mensagem real chega (via socket appMessage.create / retorno HTTP / etc.),
// a temporária é substituída pela real sem duplicatas.
//
// Regras:
// - optimisticId = string gerada no frontend (crypto.randomUUID() quando
//   disponível, se não "opt_" + Date.now() + random). Tem prefixo para NÃO
//   colidir com IDs numéricos/bigint do banco postgres.
// - Quando ADD_MESSAGE chega com optimisticId, o reducer junta por ele.
// - Quando uma mensagem SEM optimisticId chega (ex.: eco do backend com id
//   numérico real), a reducer procura por mensagens pendentes
//   (isOptimistic === true) com o mesmo tipo/mediaUrl/body e substitui (match
//   heurístico + chave única passada pelo componente pai via replaceKey).

const ChatOptimisticContext = createContext(null);

const gerarOptimisticId = () => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		try {
			return `opt_${crypto.randomUUID()}`;
		} catch (_e) {
			/* fallback abaixo */
		}
	}
	return `opt_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
};

export const ChatOptimisticProvider = ({ children, ticketId }) => {
	// Usamos um state mensagensOptimistic que é apenas uma LISTA de objetos de
	// MENSAGEM já prontos (no formato esperado pelo reducer do
	// MessagesList). O próprio MessagesList, no useEffect de inicialização,
	// se inscreve aqui para recebê-las no dispatch via subscribe.
	//
	// Como o MessagesList usa `useReducer(reducer, [])`, ele não quer estado
	// duplicado; portanto aqui só temos o barramento de pub/sub. Quem guarda o
	// state definitivo da lista é o próprio reducer do MessagesList (1 fonte
	// da verdade).

	const callbacksRef = useRef(new Set());

	// Mantemos um Map<optimisticId, mensagem> só para o caso de alguém chamar
	// atualizarOptimistic direto do componente que disparou; a lista não é a
	// fonte da verdade, mas serve para o subscribe inicial disparar os
	// pendentes para quem acabou de montar.
	//
	// IMPORTANTE anti-duplicação: NÃO re-emite `ADD` a todo novo subscriber.
	// Apenas emite no PRIMEIRO subscribe do ciclo, depois limpa a lista.
	// Reemitir todo ADD para todo novo subscriber causa DUPLICATA quando o
	// MessagesList "remonta" (ex.: StrictMode dev, ou troca de ticket rápida,
	// ou scrollup que força reconstrução do DOM da lista).
	const pendentesRef = useRef(new Map());
	const pendentesEntreguesRef = useRef(false);

	const publicar = useCallback((mensagem) => {
		// Guarda para o PRIMEIRO subscriber que ainda não chegou.
		pendentesRef.current.set(mensagem?.mensagem?.optimisticId || mensagem?.optimisticId || `${Date.now()}_${Math.random()}`, mensagem);
		callbacksRef.current.forEach((cb) => {
			try {
				cb(mensagem);
			} catch (_e) {
				/* no-op */
			}
		});
	}, []);

	const adicionarMensagem = useCallback(
		({
			body = "",
			mediaUrl = "",
			mediaType = "",
			fromMe = true,
			isForwarded = false,
			userId = null,
			quotedMsg = null,
			ticketId: tIdOverride = null,
			extra = {},
		} = {}) => {
			const optimisticId = gerarOptimisticId();
			const agora = new Date();
			const msg = {
				optimisticId,
				id: optimisticId,
				ticketId: Number(tIdOverride ?? ticketId),
				fromMe: Boolean(fromMe),
				body: String(body || ""),
				mediaUrl: String(mediaUrl || ""),
				mediaType: String(mediaType || ""),
				isForwarded: Boolean(isForwarded),
				userId: userId ?? null,
				quotedMsg: quotedMsg ? { ...quotedMsg } : null,
				ack: 0,
				isDeleted: false,
				isGroup: false,
				createdAt: agora.toISOString(),
				updatedAt: agora.toISOString(),
				sentFromDevice: false,
				isOptimistic: true,
				...extra,
			};
			// Marca pendentes como "ainda não entregues" para garantir que o
			// próximo subscriber NÃO vai perder este ADD caso ainda não tenha
			// se inscrito. Apenas 1 subscriber que entra NO MESMO ciclo de
			// evento (até 1 segundo) recebe este pendente.
			pendentesEntreguesRef.current = false;
			const evento = { tipo: "ADD", mensagem: msg };
			pendentesRef.current.set(optimisticId, evento);
			callbacksRef.current.forEach((cb) => {
				try {
					cb(evento);
				} catch (_e) {
					/* no-op */
				}
			});
			// Hard cap: nunca mantenha pendentes mais de 15s. Se o primeiro
			// subscriber só chegar depois disso (extremamente raro, mas por
			// segurança), melhor não criar duplicata depois do eco do socket.
			setTimeout(() => {
				pendentesRef.current.delete(optimisticId);
			}, 15000);
			return optimisticId;
		},
		[ticketId],
	);

	const atualizarMensagem = useCallback(
		(optimisticId, patch = {}, { marcarComoConfirmada = true } = {}) => {
			if (!optimisticId) return;
			const atual = pendentesRef.current.get(optimisticId)?.mensagem || undefined;
			const base = atual ? { ...atual, ...patch } : { optimisticId, ...patch, id: optimisticId };
			if (marcarComoConfirmada) {
				base.isOptimistic = false;
			}
			publicar({ tipo: "UPDATE", mensagem: base });
			if (marcarComoConfirmada) pendentesRef.current.delete(optimisticId);
		},
		[publicar],
	);

	const substituirMensagem = useCallback(
		(optimisticId, mensagemReal) => {
			if (!optimisticId || !mensagemReal) return;
			publicar({
				tipo: "REPLACE",
				optimisticId,
				mensagem: mensagemReal,
			});
			pendentesRef.current.delete(optimisticId);
		},
		[publicar],
	);

	const removerMensagem = useCallback(
		(optimisticId) => {
			if (!optimisticId) return;
			publicar({ tipo: "REMOVE", optimisticId });
			pendentesRef.current.delete(optimisticId);
		},
		[publicar],
	);

	const subscribe = useCallback((cb) => {
		if (typeof cb !== "function") return () => {};
		callbacksRef.current.add(cb);

		// Re-emite pendentes apenas uma vez, e só se já não foi entregue a
		// ninguém. Depois da primeira entrega, todo novo subscribe NÃO ganha
		// ADDs antigos — não cause duplicações.
		if (!pendentesEntreguesRef.current && pendentesRef.current.size > 0) {
			pendentesEntreguesRef.current = true;
			pendentesRef.current.forEach((entry) => {
				try {
					cb(entry?.tipo ? entry : { tipo: "ADD", mensagem: entry });
				} catch (_e) {
					/* no-op */
				}
			});
			// Assim que entregamos aos primeiros listeners, apagamos tudo —
			// evita que qualquer subscriber futuro (que já devia ter a
			// mensagem no reducer via socket ou primeiro subscribe) veja ADD
			// duplicado.
			pendentesRef.current.clear();
		}

		return () => {
			callbacksRef.current.delete(cb);
		};
	}, []);

	const valor = useMemo(
		() => ({
			ticketId,
			adicionarMensagem,
			atualizarMensagem,
			substituirMensagem,
			removerMensagem,
			subscribe,
		}),
		[
			ticketId,
			adicionarMensagem,
			atualizarMensagem,
			substituirMensagem,
			removerMensagem,
			subscribe,
		],
	);

	return (
		<ChatOptimisticContext.Provider value={valor}>
			{children}
		</ChatOptimisticContext.Provider>
	);
};

export const useChatOptimistic = () => {
	const ctx = useContext(ChatOptimisticContext);
	// Quem estiver fora do provider recebe NOOPs seguros: nunca quebra nada,
	// só não tem otimização (comportamento antigo).
	if (!ctx) {
		return {
			ticketId: null,
			adicionarMensagem: () => `opt_noop_${Date.now()}`,
			atualizarMensagem: () => {},
			substituirMensagem: () => {},
			removerMensagem: () => {},
			subscribe: () => () => {},
		};
	}
	return ctx;
};

export default ChatOptimisticContext;
